import { useState } from "react";
import {
  Settings as SettingsIcon,
  Smartphone,
  Hammer,
  Gamepad2,
  BarChart3,
  CreditCard,
  KeyRound,
  Network,
  FileClock,
  PackageOpen,
  Sparkles,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Archive,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useUser } from "@/client/backend/user";
import { useOrganization } from "@/client/backend/organization";
import { useOrganizationScopes } from "@/lib/scopes";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useSidebarExpanded } from "./sidebar-state";

type NavItem = {
  to: string;
  label: string;
  icon: typeof Sparkles;
  isActive: boolean;
  isVisible: boolean;
};

type NavSection = {
  title: string;
  items: NavItem[];
};

function OrganizationSwitcher({ collapsed = false }: { collapsed?: boolean }) {
  const { user } = useUser();
  const [organization, setOrganization] = useOrganization();
  const name = organization === "loading" ? "Loading organization..." : organization.displayName;

  return (
    <Select
      value={organization === "loading" ? "" : organization.id}
      disabled={user === "loading" || organization === "loading"}
      onValueChange={(id) => {
        if (user === "loading") return;
        const selected = user.organizations.find((org) => org.id === id);
        if (selected) setOrganization(selected);
      }}
    >
      <SelectTrigger
        aria-label={`Organization: ${name}`}
        title={name}
        className={cn(
          "h-9 gap-2 border-transparent px-2 text-left hover:bg-muted/60 [&>svg]:shrink-0",
          collapsed && "size-9 justify-center gap-0 px-0",
        )}
      >
        {!collapsed && <span className="min-w-0 truncate text-sm font-medium">{name}</span>}
      </SelectTrigger>
      <SelectContent side="top" align="start" sideOffset={8} className="max-w-[calc(100vw-2rem)]">
        {user !== "loading" &&
          user.organizations.map((org) => (
            <SelectItem key={org.id} value={org.id} className="break-words">
              {org.displayName}
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  );
}

export function Sidebar({ activePage }: { activePage?: "onboarding" | "playground" }) {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isExpanded, toggleExpanded] = useSidebarExpanded();
  const location = useLocation();
  const selectedPage =
    activePage ??
    (location.pathname === "/onboarding"
      ? "onboarding"
      : location.pathname.startsWith("/stream")
        ? "playground"
        : undefined);
  const isOnboarding = selectedPage === "onboarding";
  const isPlayground = selectedPage === "playground";
  const { user } = useUser();
  const [organization] = useOrganization();
  const { can, role } = useOrganizationScopes(organization);
  const canSeeRegistry = user !== "loading" && user.emailAddress.endsWith("@limrun.com");
  // Builds and sessions are per-instance pages reached from the instances
  // list, so the list stays highlighted while the user is on them.
  const isInstances =
    location.pathname === "/instances" ||
    location.pathname.startsWith("/builds/") ||
    location.pathname.startsWith("/sessions/");

  const sections: NavSection[] = [
    {
      title: "Workspace",
      items: [
        {
          to: "/onboarding",
          label: "Get started",
          icon: Sparkles,
          isActive: isOnboarding,
          isVisible: true,
        },
        {
          to: "/stream",
          label: "Playground",
          icon: Gamepad2,
          isActive: isPlayground,
          isVisible: true,
        },
        {
          to: "/instances",
          label: "Instances",
          icon: Smartphone,
          isActive: isInstances,
          isVisible: true,
        },
        // Gated pages sit in their natural group; the gate is temporary, so
        // the nav does not call them out as internal.
        {
          to: "/registry",
          label: "Registry",
          icon: PackageOpen,
          isActive: location.pathname === "/registry",
          isVisible: canSeeRegistry,
        },
        {
          to: "/bazel",
          label: "Bazel",
          icon: Hammer,
          isActive: location.pathname === "/bazel",
          isVisible: true,
        },
        {
          to: "/snapshots",
          label: "Snapshots",
          icon: Archive,
          isActive: location.pathname === "/snapshots" || location.pathname.startsWith("/snapshots/"),
          isVisible: can("xcode", "read"),
        },
        {
          to: "/secrets",
          label: "Secrets",
          icon: KeyRound,
          isActive: location.pathname === "/secrets",
          isVisible: canSeeRegistry && can("secret", "read"),
        },
      ],
    },
    {
      title: "Account",
      items: [
        {
          to: "/analytics",
          label: "Analytics",
          icon: BarChart3,
          isActive: location.pathname === "/analytics",
          isVisible: true,
        },
        // Nobody but an admin reaches billing, so the entry follows the role.
        {
          to: "/billing",
          label: "Billing",
          icon: CreditCard,
          isActive: location.pathname === "/billing",
          isVisible: can("billing", "read"),
        },
        { to: "/audit-logs", label: "Audit logs", icon: FileClock, isActive: location.pathname === "/audit-logs", isVisible: role === "admin" },
        {
          to: "/network",
          label: "Tunnels",
          icon: Network,
          isActive: location.pathname === "/network",
          isVisible: can("tunnel", "read"),
        },
        {
          to: "/settings",
          label: "Settings",
          icon: SettingsIcon,
          isActive: location.pathname === "/settings",
          isVisible: true,
        },
      ],
    },
  ]
    .map((section) => ({ ...section, items: section.items.filter((item) => item.isVisible) }))
    .filter((section) => section.items.length > 0);

  const toggleLabel = isExpanded ? "Collapse sidebar" : "Expand sidebar";
  const ToggleIcon = isExpanded ? PanelLeftClose : PanelLeftOpen;

  const itemClass = (isActive: boolean) =>
    cn(
      "relative flex h-8 items-center gap-2.5 rounded-md px-2.5 text-sm transition-colors",
      isActive
        ? "bg-accent/40 font-semibold text-foreground"
        : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
    );

  return (
    <>
      <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
        <SheetTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="fixed left-3 top-2.5 z-40 bg-background sm:hidden"
            aria-label="Open navigation menu"
          >
            <Menu />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="flex w-64 flex-col gap-0 p-0">
          <SheetHeader className="shrink-0 px-5 pb-2 pt-5 text-left">
            <SheetTitle>
              <img src="/logo-wordmark-dark.svg" alt="Limrun" className="hidden h-5 dark:block" />
              <img src="/logo-wordmark.svg" alt="Limrun" className="h-5 dark:hidden" />
            </SheetTitle>
            <SheetDescription className="sr-only">
              Navigate between pages in the Limrun console.
            </SheetDescription>
          </SheetHeader>
          <nav
            aria-label="Main navigation"
            className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-3"
          >
            {sections.map((section) => (
              <div key={section.title} className="flex flex-col gap-0.5">
                <p className="sr-only">{section.title}</p>
                {section.items.map(({ to, label, icon: Icon, isActive }) => (
                  <Link
                    key={to}
                    to={to}
                    aria-current={isActive ? "page" : undefined}
                    onClick={() => setIsMobileOpen(false)}
                    className={itemClass(isActive)}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span>{label}</span>
                  </Link>
                ))}
              </div>
            ))}
          </nav>
          <div className="shrink-0 border-t border-border/50 p-3">
            <OrganizationSwitcher />
          </div>
        </SheetContent>
      </Sheet>

      <aside
        className="fixed inset-y-0 left-0 z-30 hidden w-(--sidebar-width) flex-col border-r border-border/50 bg-background transition-[width] duration-200 sm:flex"
        data-expanded={isExpanded}
      >
        <TooltipProvider delayDuration={0}>
          <div
            className={cn("flex h-14 shrink-0 items-center px-3", !isExpanded && "justify-center")}
          >
            <Link
              to="/onboarding"
              className="flex items-center px-2"
              aria-label="Limrun console home"
            >
              {isExpanded ? (
                <>
                  <img src="/logo-wordmark.svg" alt="" className="h-5 dark:hidden" />
                  <img src="/logo-wordmark-dark.svg" alt="" className="hidden h-5 dark:block" />
                </>
              ) : (
                <img src="/logo-mark.svg" alt="" className="h-[18px]" />
              )}
            </Link>
          </div>

          <nav
            aria-label="Main navigation"
            className={cn(
              "flex flex-1 flex-col gap-4 overflow-x-hidden overflow-y-auto pt-5",
              isExpanded ? "px-3" : "items-center px-2",
            )}
          >
            {sections.map((section) => (
              <div
                key={section.title}
                className={cn(
                  "flex flex-col gap-0.5",
                  section.title === "Account" && "mt-auto pt-6",
                  !isExpanded && "items-center",
                )}
              >
                {isExpanded ? (
                  <p className="sr-only">{section.title}</p>
                ) : (
                  <div className="mb-1 h-px w-4 bg-border" aria-hidden />
                )}
                {section.items.map(({ to, label, icon: Icon, isActive }) =>
                  isExpanded ? (
                    <Link
                      key={to}
                      to={to}
                      aria-current={isActive ? "page" : undefined}
                      className={itemClass(isActive)}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="truncate">{label}</span>
                    </Link>
                  ) : (
                    <Tooltip key={to}>
                      <TooltipTrigger asChild>
                        <Link
                          to={to}
                          aria-current={isActive ? "page" : undefined}
                          className={cn(itemClass(isActive), "w-8 justify-center px-0")}
                        >
                          <Icon className="h-4 w-4" />
                          <span className="sr-only">{label}</span>
                        </Link>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="z-100">
                        {label}
                      </TooltipContent>
                    </Tooltip>
                  ),
                )}
              </div>
            ))}
          </nav>

          <div
            className={cn("mt-3 shrink-0 py-2", isExpanded ? "px-3" : "flex justify-center px-2")}
          >
            {isExpanded ? (
              <button
                type="button"
                onClick={toggleExpanded}
                aria-label={toggleLabel}
                className={cn(itemClass(false), "w-full")}
              >
                <ToggleIcon className="h-4 w-4 shrink-0" />
                <span>Collapse</span>
              </button>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={toggleExpanded}
                    aria-label={toggleLabel}
                    className={cn(itemClass(false), "w-8 justify-center px-0")}
                  >
                    <ToggleIcon className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="z-100">
                  {toggleLabel}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          <div
            className={cn(
              "shrink-0 border-t border-border/50 py-3",
              isExpanded ? "px-3" : "flex justify-center px-2",
            )}
          >
            <OrganizationSwitcher collapsed={!isExpanded} />
          </div>
        </TooltipProvider>
      </aside>
    </>
  );
}
